import { useMemo, useState } from 'react';
import { Search, Plus, Activity, CheckCircle2, Target, MessageCircle } from 'lucide-react';
import { Link } from 'react-router';
import { Avatar } from '../components/Avatar';
import { KineHeader } from '../components/kine/KineHeader';
import { KineSidebar } from '../components/kine/KineSidebar';
import { formatRelativeDate } from '../../lib/utils';
import {
  useKineDashboard,
  type PacienteDashboard,
  type EstadoPaciente,
  type AdherenciaReason,
} from '@/lib/supabase/queries/useKineDashboard';
import { useCrearPaciente } from '@/lib/supabase/queries/useCrearPaciente';
import {
  PatientFormDialog,
  type PatientFormCreateData,
  type PatientFormEditData,
} from '../components/patients/PatientFormDialog';
import { InvitacionModal } from '../components/patients/InvitacionModal';
import {
  PatientFilterChips,
  type FilterKey,
} from '../components/patients/PatientFilterChips';
import { PatientQuickActionsMenu } from '../components/patients/PatientQuickActionsMenu';

interface InvitacionState {
  signupToken: string;
  pacienteNombre: string;
  expiresAt: string;
  variant: 'creada' | 'actual';
}

/** Aplica filtros AND + search a la lista de pacientes. Empty filter set
 *  = no filter (todos pasan). Empty search = no filter (todos pasan). */
function applyFilters(
  pacientes: PacienteDashboard[],
  active: Set<FilterKey>,
  search: string,
): PacienteDashboard[] {
  const q = search.trim().toLowerCase();
  return pacientes.filter((p) => {
    if (active.has('needsReview') && !p.needsReview) return false;
    if (active.has('lowAdherence') && !p.lowAdherence) return false;
    if (active.has('hasPain') && !p.hasPain) return false;
    if (active.has('justJoined') && !p.justJoined) return false;
    if (q) {
      const haystack = `${p.nombre} ${p.apellido} ${p.diagnostico ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

/** Cuenta cuántos pacientes matchean SOLO ese filtro (no la intersección
 *  con otros). Permite mostrar "(N)" en cada chip con info útil incluso
 *  cuando otros chips ya están activos. */
function deriveFilterCounts(
  pacientes: PacienteDashboard[],
): Record<FilterKey, number> {
  let needsReview = 0;
  let lowAdherence = 0;
  let hasPain = 0;
  let justJoined = 0;
  for (const p of pacientes) {
    if (p.needsReview) needsReview++;
    if (p.lowAdherence) lowAdherence++;
    if (p.hasPain) hasPain++;
    if (p.justJoined) justJoined++;
  }
  return { needsReview, lowAdherence, hasPain, justJoined };
}

export function DashboardKine() {
  const { pacientes, metricas, error, loading, refetch } = useKineDashboard();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [invitacion, setInvitacion] = useState<InvitacionState | null>(null);
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set());
  const [search, setSearch] = useState('');
  const crearPaciente = useCrearPaciente();

  const filterCounts = useMemo(
    () => (pacientes ? deriveFilterCounts(pacientes) : undefined),
    [pacientes],
  );
  const filteredPacientes = useMemo(
    () => (pacientes ? applyFilters(pacientes, activeFilters, search) : null),
    [pacientes, activeFilters, search],
  );
  const isFiltered = activeFilters.size > 0 || search.trim().length > 0;

  const handleCreate = async (
    data: PatientFormCreateData | PatientFormEditData,
  ) => {
    const result = await crearPaciente.crear(data);
    if (!result) return;
    setCreateOpen(false);
    crearPaciente.reset();
    setInvitacion({
      signupToken: result.signupToken,
      pacienteNombre: `${data.nombre} ${data.apellido}`,
      expiresAt: result.expiresAt,
      variant: 'creada',
    });
    void refetch();
  };

  const toggleFilter = (key: FilterKey) =>
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const handleCompartirWhatsapp = (pacienteId: string, signupToken: string) => {
    const p = pacientes?.find((x) => x.id === pacienteId);
    if (!p) return;
    // expiresAt no lo tenemos en el shape del dashboard — el InvitacionModal
    // lo necesita para mostrar "vence en X días". Workaround: pasamos hoy + 7d
    // como aproximación. Para precisión exacta, navegar a DetallePaciente.
    // (Trade-off aceptable: quick share desde dashboard sin fetch extra.)
    const approxExpires = new Date(
      Date.now() + 7 * 86_400_000,
    ).toISOString();
    setInvitacion({
      signupToken,
      pacienteNombre: `${p.nombre} ${p.apellido}`,
      expiresAt: approxExpires,
      variant: 'actual',
    });
  };

  return (
    <div className="min-h-screen bg-background transition-colors duration-200">
      <KineHeader
        kineNombre={metricas?.kineNombre}
        onMenuClick={() => setDrawerOpen(true)}
      />

      <div className="flex">
        <KineSidebar
          activeKey="pacientes"
          mobileOpen={drawerOpen}
          onMobileClose={() => setDrawerOpen(false)}
        />

        <div className="flex-1 p-4 md:p-6 min-w-0">
          <div className="max-w-[1200px]">
            <h1 className="text-3xl">Mis pacientes</h1>
            <p className="text-muted-foreground mt-1.5 text-sm">
              Resumen de tu práctica esta semana
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mt-6">
              <StatCard
                icon={<Activity className="w-5 h-5 text-cyan-600" strokeWidth={1.5} />}
                label="Pacientes"
                value={metricas?.totalPatients}
                sub="Pacientes activos"
                loading={loading}
              />
              <StatCard
                icon={<CheckCircle2 className="w-5 h-5 text-cyan-600" strokeWidth={1.5} />}
                label="Adherencia"
                value={
                  metricas?.avgAdherence !== null &&
                  metricas?.avgAdherence !== undefined
                    ? `${metricas.avgAdherence}%`
                    : '—'
                }
                sub="Promedio últimos 7 días"
                loading={loading}
              />
              <StatCard
                icon={<Target className="w-5 h-5 text-cyan-600" strokeWidth={1.5} />}
                label="Sesiones"
                value={metricas?.activeSessions}
                sub="Completadas esta semana"
                loading={loading}
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-6">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar paciente..."
                  className="w-full pl-11 pr-4 py-2.5 text-sm bg-input-background border border-border rounded-full transition-all duration-200 ease-out focus:ring-2 focus:ring-ring focus:border-transparent"
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  crearPaciente.reset();
                  setCreateOpen(true);
                }}
                className="bg-primary text-primary-foreground px-5 py-2.5 rounded-full flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold shadow-sm transition-all duration-200 ease-out hover:shadow-md"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Nuevo paciente
              </button>
            </div>

            {/* Filter chips — multi-select AND. Counts derivados de la lista
                completa (no de la intersección), para que cada chip muestre
                cuántos matchean ese filtro individualmente. */}
            <div className="mt-4">
              <PatientFilterChips
                active={activeFilters}
                onToggle={toggleFilter}
                onClear={() => setActiveFilters(new Set())}
                counts={filterCounts}
              />
            </div>

            {/* Mobile: card list */}
            <div className="md:hidden mt-6 space-y-3">
              {loading && <CardListSkeleton />}
              {error && !loading && <CardListError message={error} />}
              {!loading && !error && filteredPacientes && filteredPacientes.length === 0 && (
                isFiltered ? <CardListEmptyFiltered /> : <CardListEmpty />
              )}
              {!loading && !error && filteredPacientes && filteredPacientes.length > 0 &&
                filteredPacientes.map((p, idx) => (
                  <PacienteCard
                    key={p.id}
                    paciente={p}
                    colorIndex={idx}
                    onCompartirWhatsapp={(token) =>
                      handleCompartirWhatsapp(p.id, token)
                    }
                  />
                ))}
            </div>

            {/* Desktop: tabla */}
            <div className="hidden md:block bg-card rounded-xl mt-6 border border-border overflow-hidden shadow-sm">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left">
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">Paciente</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">Diagnóstico</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">Última sesión</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">Adherencia</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">Estado</th>
                    <th className="px-2 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <TableSkeleton />}
                  {error && !loading && <TableError message={error} />}
                  {!loading && !error && filteredPacientes && filteredPacientes.length === 0 && (
                    isFiltered ? <TableEmptyFiltered /> : <TableEmpty />
                  )}
                  {!loading && !error && filteredPacientes && filteredPacientes.length > 0 &&
                    filteredPacientes.map((p, idx) => (
                      <PacienteRow
                        key={p.id}
                        paciente={p}
                        colorIndex={idx}
                        onCompartirWhatsapp={(token) =>
                          handleCompartirWhatsapp(p.id, token)
                        }
                      />
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <PatientFormDialog
        open={createOpen}
        onOpenChange={(o) => {
          if (!crearPaciente.loading) setCreateOpen(o);
        }}
        mode="crear"
        submitting={crearPaciente.loading}
        errorMessage={crearPaciente.error}
        onSave={handleCreate}
      />

      {invitacion && (
        <InvitacionModal
          open={invitacion !== null}
          onOpenChange={(o) => {
            if (!o) setInvitacion(null);
          }}
          signupToken={invitacion.signupToken}
          pacienteNombre={invitacion.pacienteNombre}
          expiresAt={invitacion.expiresAt}
          variant={invitacion.variant}
        />
      )}
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────

function StatCard({
  icon,
  label,
  value,
  sub,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string | null | undefined;
  sub: string;
  loading: boolean;
}) {
  return (
    <div className="bg-card rounded-xl p-5 border border-border shadow-sm transition-all duration-200 ease-out hover:shadow-md hover:border-primary/30">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-cyan-400/20 flex items-center justify-center shrink-0">
          {icon}
        </div>
        <span className="text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">
          {label}
        </span>
      </div>
      {loading ? (
        <div className="h-8 w-16 bg-muted rounded animate-pulse" />
      ) : (
        <div className="text-3xl font-bold text-primary tabular-nums">
          {value ?? '—'}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-1">{sub}</p>
    </div>
  );
}

/** Tooltip contextual cuando la adherencia no se pudo calcular. Ver
 *  F-01 en SPEC. Cada motivo → copy corto. */
function tooltipForAdherenciaReason(
  reason: AdherenciaReason | null,
): string {
  switch (reason) {
    case 'sin_frecuencia':
      return 'Falta configurar frecuencia semanal en el plan';
    case 'ventana_corta':
      return 'Plan recién iniciado, todavía sin datos de adherencia';
    case 'sin_plan':
      return 'Sin plan activo';
    case 'no_encontrado':
    case 'error':
      return 'Error al calcular';
    default:
      return '';
  }
}

function PacienteRow({
  paciente,
  colorIndex,
  onCompartirWhatsapp,
}: {
  paciente: PacienteDashboard;
  colorIndex: number;
  onCompartirWhatsapp: (signupToken: string) => void;
}) {
  const fullName = `${paciente.nombre} ${paciente.apellido}`;
  const ultimaSesionLabel = paciente.ultimaSesionAt
    ? formatRelativeDate(new Date(paciente.ultimaSesionAt))
    : 'Sin sesiones';

  return (
    <tr className="border-b border-border hover:bg-muted/30 cursor-pointer transition-all duration-200 ease-out">
      <td className="px-5 py-3">
        <Link to={`/paciente/${paciente.id}`} className="flex items-center gap-3">
          <Avatar name={fullName} colorIndex={colorIndex} />
          <span className="font-medium">{fullName}</span>
        </Link>
      </td>
      <td className="px-5 py-3 text-muted-foreground">
        {paciente.diagnostico ?? '—'}
      </td>
      <td className="px-5 py-3 text-muted-foreground">{ultimaSesionLabel}</td>
      <td className="px-5 py-3 tabular-nums">
        {paciente.adherencia !== null ? (
          <span className="font-semibold">{paciente.adherencia}%</span>
        ) : (
          <span
            className="text-muted-foreground cursor-help"
            title={tooltipForAdherenciaReason(paciente.adherenciaReason)}
          >
            —
          </span>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center gap-2">
          {paciente.invitationStatus === 'invited' ? (
            <InvitadoBadge />
          ) : (
            <EstadoBadge estado={paciente.estado} />
          )}
          {paciente.unreadMensajes > 0 && (
            <UnreadMensajesBadge count={paciente.unreadMensajes} />
          )}
        </div>
      </td>
      <td className="px-2 py-3 w-10">
        <PatientQuickActionsMenu
          paciente={{
            id: paciente.id,
            invitationStatus: paciente.invitationStatus,
            signupToken: paciente.signupToken,
          }}
          onCompartirWhatsapp={onCompartirWhatsapp}
        />
      </td>
    </tr>
  );
}

/** Badge para paciente que el kine invitó pero todavía no completó signup.
 *  Distinto del estado derivado (Al día / En riesgo / etc) — distingue
 *  "esperando que el paciente active la cuenta" vs "activo sin actividad
 *  reciente". */
function InvitadoBadge() {
  return (
    <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all duration-200 ease-out bg-blue-50 text-blue-900 border border-blue-200">
      Invitado
    </span>
  );
}

/** Badge per row con count de mensajes del paciente sin leer por el kine.
 *  Sprint 3 B6 — ayuda al kine a priorizar quién le escribió. */
function UnreadMensajesBadge({ count }: { count: number }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-primary text-primary-foreground tabular-nums"
      title={`${count} mensaje${count === 1 ? '' : 's'} sin leer`}
      aria-label={`${count} mensaje${count === 1 ? '' : 's'} sin leer`}
    >
      <MessageCircle className="w-3 h-3" strokeWidth={2} />
      {count > 99 ? '99+' : count}
    </span>
  );
}

function EstadoBadge({ estado }: { estado: EstadoPaciente }) {
  // Status colors normalizados con paleta softer (light bg + dark text +
  // border sutil) en lugar de solid fill saturado:
  // - Al día: teal primary (success)
  // - En riesgo: amber soft (warning, recordatorio amistoso)
  // - Atrasado: red soft (critical, requiere acción — sin look "drugstore")
  // - Sin sesiones: muted neutral
  // Coral/accent NO se usa para estados — reservado a alerts raros.
  const styles: Record<EstadoPaciente, string> = {
    'Al día': 'bg-primary/10 text-primary border border-primary/20',
    'En riesgo': 'bg-amber-100 text-amber-900 border border-amber-200',
    'Atrasado': 'bg-red-100 text-red-900 border border-red-200',
    'Sin sesiones': 'bg-muted text-muted-foreground border border-border',
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-all duration-200 ease-out ${styles[estado]}`}
    >
      {estado}
    </span>
  );
}

function TableSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} className="border-b border-border">
          <td className="px-5 py-3">
            <div className="flex items-center gap-3 animate-pulse">
              <div className="w-10 h-10 rounded-full bg-muted" />
              <div className="h-4 w-32 bg-muted rounded" />
            </div>
          </td>
          <td className="px-5 py-3"><div className="h-4 w-40 bg-muted rounded animate-pulse" /></td>
          <td className="px-5 py-3"><div className="h-4 w-20 bg-muted rounded animate-pulse" /></td>
          <td className="px-5 py-3"><div className="h-4 w-12 bg-muted rounded animate-pulse" /></td>
          <td className="px-5 py-3"><div className="h-6 w-20 bg-muted rounded-full animate-pulse" /></td>
          <td className="px-2 py-3 w-10"></td>
        </tr>
      ))}
    </>
  );
}

function TableError({ message }: { message: string }) {
  return (
    <tr>
      <td colSpan={6} className="px-6 py-12 text-center">
        <p className="font-semibold text-orange-600 mb-1">
          No pudimos cargar tus pacientes
        </p>
        <p className="text-sm text-muted-foreground">{message}</p>
      </td>
    </tr>
  );
}

function TableEmpty() {
  return (
    <tr>
      <td colSpan={6} className="px-6 py-12 text-center">
        <p className="font-semibold mb-1">Aún no tenés pacientes asignados</p>
        <p className="text-sm text-muted-foreground">
          Creá el primero con el botón "Nuevo paciente" arriba.
        </p>
      </td>
    </tr>
  );
}

function TableEmptyFiltered() {
  return (
    <tr>
      <td colSpan={6} className="px-6 py-12 text-center">
        <p className="font-semibold mb-1">Ningún paciente coincide con los filtros</p>
        <p className="text-sm text-muted-foreground">
          Probá limpiando los filtros o cambiando la búsqueda.
        </p>
      </td>
    </tr>
  );
}

// ─── Mobile card list (mismo data shape, layout vertical) ────────────

function PacienteCard({
  paciente,
  colorIndex,
  onCompartirWhatsapp,
}: {
  paciente: PacienteDashboard;
  colorIndex: number;
  onCompartirWhatsapp: (signupToken: string) => void;
}) {
  const fullName = `${paciente.nombre} ${paciente.apellido}`;
  const ultimaSesionLabel = paciente.ultimaSesionAt
    ? formatRelativeDate(new Date(paciente.ultimaSesionAt))
    : 'Sin sesiones';
  const adherenciaLabel =
    paciente.adherencia !== null ? `${paciente.adherencia}%` : '—';
  const adherenciaTooltip =
    paciente.adherencia === null
      ? tooltipForAdherenciaReason(paciente.adherenciaReason)
      : undefined;

  // Mobile: card wrapeada en <div> (no Link) para que el kebab del menu
  // pueda manejar su propio click sin race con la navegación del row.
  // El click del card-body sigue navegando vía useNavigate inline.
  return (
    <div className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)] transition-all duration-200 ease-out hover:bg-muted/30 relative">
      <Link
        to={`/paciente/${paciente.id}`}
        className="absolute inset-0 z-0"
        aria-label={`Ver detalle de ${fullName}`}
      />
      <div className="flex items-center gap-3 mb-3 relative z-10 pointer-events-none">
        <Avatar name={fullName} colorIndex={colorIndex} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold truncate">{fullName}</p>
          <p className="text-xs text-muted-foreground truncate">
            {paciente.diagnostico ?? '—'}
          </p>
        </div>
        {paciente.invitationStatus === 'invited' ? (
          <InvitadoBadge />
        ) : (
          <EstadoBadge estado={paciente.estado} />
        )}
        {paciente.unreadMensajes > 0 && (
          <UnreadMensajesBadge count={paciente.unreadMensajes} />
        )}
        <div className="pointer-events-auto">
          <PatientQuickActionsMenu
            paciente={{
              id: paciente.id,
              invitationStatus: paciente.invitationStatus,
              signupToken: paciente.signupToken,
            }}
            onCompartirWhatsapp={onCompartirWhatsapp}
          />
        </div>
      </div>
      <div className="flex justify-between text-sm text-muted-foreground relative z-10 pointer-events-none">
        <span>{ultimaSesionLabel}</span>
        <span className="tabular-nums" title={adherenciaTooltip}>
          Adherencia {adherenciaLabel}
        </span>
      </div>
    </div>
  );
}

function CardListSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="bg-card rounded-2xl p-4 border border-border shadow-[0_1px_2px_rgba(15,23,32,0.04),0_1px_3px_rgba(15,23,32,0.06)]"
        >
          <div className="flex items-center gap-3 mb-3 animate-pulse">
            <div className="w-10 h-10 rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-muted rounded" />
              <div className="h-3 w-40 bg-muted rounded" />
            </div>
            <div className="h-6 w-20 bg-muted rounded-full" />
          </div>
          <div className="flex justify-between animate-pulse">
            <div className="h-3 w-20 bg-muted rounded" />
            <div className="h-3 w-24 bg-muted rounded" />
          </div>
        </div>
      ))}
    </>
  );
}

function CardListError({ message }: { message: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border text-center">
      <p className="font-semibold text-orange-600 mb-1">
        No pudimos cargar tus pacientes
      </p>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function CardListEmpty() {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border text-center">
      <p className="font-semibold mb-1">Aún no tenés pacientes asignados</p>
      <p className="text-sm text-muted-foreground">
        Creá el primero con el botón "Nuevo paciente" arriba.
      </p>
    </div>
  );
}

function CardListEmptyFiltered() {
  return (
    <div className="bg-card rounded-2xl p-6 border border-border text-center">
      <p className="font-semibold mb-1">Ningún paciente coincide con los filtros</p>
      <p className="text-sm text-muted-foreground">
        Probá limpiando los filtros o cambiando la búsqueda.
      </p>
    </div>
  );
}
